import { CheckRounded, EditOutlined } from "@mui/icons-material";
import { Box, Stack, Button, Skeleton, IconButton, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionSkeleton,
  isWhitespace,
  MaterialSymbolIcon,
  MaterialSymbols,
  TextEditor,
  TextViewer,
  type TextEditorRef,
} from "src/components";
import { useSession } from "src/hooks";
import { IssueDto, useEditIssueDecision } from "src/http";

interface Props {
  issue?: IssueDto;
  isLoading: boolean;
}

const IssueDecision = (props: Props) => {
  const user = useSession();
  const { mutate: editIssueDecision, isLoading: isSavingDecision } = useEditIssueDecision();
  const [isEditing, setIsEditing] = useState(false);
  const [decision, setDecision] = useState<string | undefined>(undefined);
  const editorRef = useRef<TextEditorRef | null>(null);
  const issueHasDecision = !isWhitespace(props.issue?.decision?.value ?? "");

  useEffect(() => {
    if (isEditing) {
      editorRef.current?.focus();
    }
  }, [isEditing]);

  function handleSaveClicked() {
    if (!props.issue) {
      return;
    }

    editIssueDecision({
      issueGuid: props.issue.guid,
      decision: decision ?? props.issue.decision?.value ?? "",
      teamSlug: props.issue.assignedTeam?.slug,
      createdByUserId: props.issue.createdByUser.userId,
      modifiedByUser: {
        userId: user.userId,
        displayName: user.displayName,
        enabled: true,
        profilePhotoSmallUrl: undefined,
      },
    });

    setDecision(undefined);
    setIsEditing(false);
  }

  function handleCancelClicked() {
    setDecision(undefined);
    setIsEditing(false);
  }

  return props.isLoading || props.issue === undefined ? (
    <AccordionSkeleton renderSkeleton={() => <Skeleton variant="rectangular" height={24} />} />
  ) : (
    <Accordion
      title={(expanded) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <MaterialSymbolIcon symbol={MaterialSymbols.Gavel} sx={{ color: "text.secondary" }} />
          <Typography variant="label">Decision</Typography>
          {issueHasDecision && !expanded && <CheckRounded sx={{ color: "text.secondary" }} />}
        </Stack>
      )}
      initialExpanded={issueHasDecision}
      accordionDetailsSx={{ px: 0.5, pb: 0.5 }}
    >
      <Box sx={{ display: "flex" }}>
        {isEditing ? (
          <Stack spacing={0.5} sx={{ flex: 1, zIndex: 2 }}>
            <TextEditor
              ref={editorRef}
              initialValue={decision ?? props.issue?.decision?.value ?? ""}
              onChange={(value) => setDecision(value)}
            />
            <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
              <Button variant="text" onClick={handleCancelClicked}>
                Cancel
              </Button>
              <Button variant="tertiary" disabled={isSavingDecision} onClick={handleSaveClicked}>
                Save
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" sx={{ flex: 1, pl: 0.5 }}>
            <Stack spacing={0.25} sx={{ flex: 1 }}>
              <TextViewer
                placeholder={"No decision has been captured yet."}
                value={props.issue.decision?.value ?? ""}
              />
              {props.issue.decision && (
                <Typography variant="caption">
                  Last updated by {props.issue.decision?.modifiedByUser.displayName} on{" "}
                  {DateTime.fromISO(props.issue.decision?.modifiedDateTimeUtc ?? "", { zone: "utc" })
                    .toLocal()
                    .toLocaleString(DateTime.DATETIME_MED)}
                </Typography>
              )}
            </Stack>

            {props.issue.permissions.userCanEditDecision && (
              <IconButton size="small" sx={{ alignSelf: "start" }} onClick={() => setIsEditing(true)}>
                <EditOutlined />
              </IconButton>
            )}
          </Stack>
        )}
      </Box>
    </Accordion>
  );
};

export { IssueDecision };
